import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, asList } from '../lib/api';
import { AuthImage } from '../components/AuthImage';
import { CaptureOff } from '../components/CaptureOff';
import { useCaptureModes } from '../lib/captureModes';

/**
 * ═══ THE PHOTO ROLL ═══
 *
 * A class as forty-five faces. The teacher taps the three who are not there and presses Save.
 *
 * ⚠ THE POINT IS RECOGNITION, NOT READING. A list of names is scanned linearly and takes three to
 * five minutes of every period — which is why so many registers are filled in afterwards from
 * memory, and why attendance data in most schools is worth less than everyone pretends. A grid of
 * faces is recognised at a glance: the teacher is looking at the room and looking at the screen and
 * matching, not reading.
 *
 * ⚠ EVERYONE STARTS PRESENT. In a class of forty-five, forty-two are. Starting blank makes the
 * teacher take forty-five decisions to record three, and a screen that costs more than the paper it
 * replaces is a screen that gets abandoned by October.
 *
 * ⚠ AND IT IS BUILT FOR A PHONE HELD IN ONE HAND at the front of a class. Big targets, a summary
 * that is always visible, and a Save bar that does not move.
 */

interface Row {
  studentId: string; admissionNumber: string; fullName: string;
  classLabel: string | null; status: string; hasPhoto: boolean;
}
interface Roster {
  institutionId: string; date: string; classLabel: string | null;
  alreadyMarked: boolean; students: Row[];
}
interface Group { id: string; label: string; people: number }

const today = () => new Date().toISOString().slice(0, 10);

/** ⚠ Present and Absent are the two taps. Late and Excused exist in the domain and are reachable
 *  from the long-press menu — putting four states on the main tap would make the common case slower
 *  to serve the rare one. */
type Status = 'Present' | 'Absent' | 'Late' | 'Excused';

export function PhotoRollPage() {
  const { allows: captureAllows, resolved: captureResolved } = useCaptureModes();
  const [institutions, setInstitutions] = useState<Array<{ id: string; name: string }>>([]);
  const [institutionId, setInstitutionId] = useState('');
  const [classes, setClasses] = useState<Group[]>([]);
  const [classLabel, setClassLabel] = useState('');
  const [date, setDate] = useState(today());

  const [roster, setRoster] = useState<Roster | null>(null);
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [menuFor, setMenuFor] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Array<{ id: string; name: string }>>('/api/institutions')
      .then((xs) => { setInstitutions(xs); setInstitutionId((i) => i || xs[0]?.id || ''); })
      .catch(() => undefined);
    api.get<Group[]>('/api/exports/register/groups?scope=students')
      .then((r) => setClasses(asList(r))).catch(() => setClasses([]));
  }, []);

  const load = useCallback(async () => {
    if (!institutionId || !classLabel) { setRoster(null); return; }
    setBusy(true); setError(null); setSaved(false);
    try {
      const q = new URLSearchParams({ institutionId, date, classLabel });
      const r = await api.get<Roster>(`/api/attendance/roster?${q}`);
      setRoster(r);
      // ⚠ Seeded from the server's answer, which already carries "unmarked means present" and, when
      // the register was taken earlier, what it was actually marked as. Seeding to Present blindly
      // would let a second teacher silently overwrite the first one's absences.
      setStatus(Object.fromEntries(r.students.map((s) => [s.studentId, s.status as Status])));
    } catch (e) {
      setRoster(null);
      setError(e instanceof ApiError ? e.message : 'Could not load the class.');
    } finally { setBusy(false); }
  }, [institutionId, classLabel, date]);

  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => {
    const v = Object.values(status);
    return {
      present: v.filter((s) => s === 'Present').length,
      absent: v.filter((s) => s === 'Absent').length,
      late: v.filter((s) => s === 'Late').length,
      excused: v.filter((s) => s === 'Excused').length,
      total: v.length,
    };
  }, [status]);

  const toggle = (id: string) => {
    setSaved(false);
    setStatus((s) => ({ ...s, [id]: s[id] === 'Absent' ? 'Present' : 'Absent' }));
  };

  const set = (id: string, v: Status) => {
    setSaved(false);
    setStatus((s) => ({ ...s, [id]: v }));
    setMenuFor(null);
  };

  const save = async () => {
    if (!roster) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/attendance/mark', {
        institutionId, date, classLabel,
        entries: roster.students.map((s) => ({
          studentId: s.studentId, status: status[s.studentId] ?? 'Present', remark: null,
        })),
      });
      setSaved(true);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the register.');
    } finally { setBusy(false); }
  };

  // ⚠ AFTER every hook, never before one. An early return above a hook changes the hook order
  // between renders, which React fails on rather than warns about.
  if (captureResolved && !captureAllows('PhotoRoll'))
    return <CaptureOff mode="The photo roll" blurb="No institution in this school has it switched on." />;

  return (
    <div style={{ paddingBottom: 92 }}>
      <div style={{ marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Attendance</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Photo roll</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '64ch' }}>
          Everyone starts present. Tap the ones who are not here, then Save. Long-press a face for
          late or excused.
        </p>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 12, display: 'flex', gap: 10,
                                     flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {institutions.length > 1 && (
          <div>
            <label className="label">Institution</label>
            <select className="input" value={institutionId}
              onChange={(e) => setInstitutionId(e.target.value)}>
              {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
        )}
        <div style={{ minWidth: 170 }}>
          <label className="label">Class</label>
          <select className="input" value={classLabel} style={{ width: '100%' }}
            onChange={(e) => setClassLabel(e.target.value)}>
            <option value="">Choose a class</option>
            {classes.map((c) => <option key={c.id} value={c.label}>{c.label} · {c.people}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Date</label>
          {/* ⚠ Capped at today. A register for next Tuesday is always a mistake, and a phone's date
              picker makes it a one-tap mistake. */}
          <input className="input" type="date" value={date} max={today()}
            onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 12, color: 'var(--danger)', fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {/* ⚠ SAID BEFORE THEY START TAPPING, not after they save. A second teacher opening a register
          somebody already took needs to know they are editing, not creating — otherwise the first
          teacher's absences quietly become somebody else's "everyone present". */}
      {roster?.alreadyMarked && !saved && (
        <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13.5,
                                       borderLeft: '3px solid var(--warn)' }}>
          This register was already taken for {date}. You are editing it — what you save replaces it.
        </div>
      )}

      {!roster && !busy && (
        <div className="card" style={{ padding: 22, color: 'var(--ink-3)' }}>
          Choose a class to see the faces.
        </div>
      )}

      {roster && roster.students.length === 0 && (
        <div className="card" style={{ padding: 22, color: 'var(--ink-3)' }}>
          Nobody is on the roll for that class.
        </div>
      )}

      {/* ── the faces ── */}
      {roster && roster.students.length > 0 && (
        <div style={{
          display: 'grid', gap: 10,
          // ⚠ auto-fill with a minimum, not a fixed column count. On a phone that is three across,
          // on a laptop eight — and the teacher's phone is the case that matters.
          gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
        }}>
          {roster.students.map((s) => {
            const st = status[s.studentId] ?? 'Present';
            const absent = st === 'Absent';
            const tint = absent ? 'var(--danger)'
              : st === 'Late' ? 'var(--warn)'
              : st === 'Excused' ? 'var(--ink-3)' : 'transparent';

            return (
              <button key={s.studentId}
                onClick={() => toggle(s.studentId)}
                onContextMenu={(e) => { e.preventDefault(); setMenuFor(s); }}
                style={{
                  border: `2px solid ${absent || st !== 'Present' ? tint : 'var(--line)'}`,
                  borderRadius: 12, padding: 0, overflow: 'hidden', background: 'var(--surface)',
                  cursor: 'pointer', font: 'inherit', textAlign: 'left',
                  // ⚠ The absent card is dimmed AND marked, never colour alone. A teacher with any
                  // degree of colour blindness has to be able to take a register.
                  opacity: absent ? 0.55 : 1,
                }}>
                <div style={{ position: 'relative', aspectRatio: '3 / 4', background: 'var(--surface-2)' }}>
                  {s.hasPhoto
                    ? <AuthImage url={`/api/students/${s.studentId}/photo`} alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ display: 'grid', placeItems: 'center', height: '100%',
                                    fontSize: 22, fontWeight: 650, color: 'var(--ink-3)' }}>
                        {/* ⚠ Initials, not an empty box. A child with no photograph still has to be
                            findable in a grid of forty-five, and a blank tile is the one nobody
                            looks at. */}
                        {s.fullName.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('')}
                      </div>}

                  {st !== 'Present' && (
                    <div style={{
                      position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                      background: 'rgba(255,255,255,.55)',
                    }}>
                      <span style={{
                        background: tint, color: '#fff', fontSize: 11, fontWeight: 700,
                        padding: '3px 9px', borderRadius: 20, letterSpacing: '.04em',
                      }}>{st.toUpperCase()}</span>
                    </div>
                  )}
                </div>

                <div style={{ padding: '6px 7px 8px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.25,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.fullName}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{s.admissionNumber}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── the save bar ── */}
      {roster && roster.students.length > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
          background: 'var(--surface)', borderTop: '1px solid var(--line)',
          padding: '10px 16px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
        }}>
          {/* ⚠ THE COUNTS ARE THE CHECK. A register saved with 45 present and 0 absent every day is
              a register nobody is really taking — showing the number as they tap is what makes an
              accidental blank save visible before it happens rather than in a report in March. */}
          <div style={{ fontSize: 13.5 }}>
            <strong>{counts.present}</strong> present
            {counts.absent > 0 && <> · <strong style={{ color: 'var(--danger)' }}>{counts.absent}</strong> absent</>}
            {counts.late > 0 && <> · <strong style={{ color: 'var(--warn)' }}>{counts.late}</strong> late</>}
            {counts.excused > 0 && <> · {counts.excused} excused</>}
            <span style={{ color: 'var(--ink-3)' }}> of {counts.total}</span>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            {saved && <span style={{ fontSize: 13, color: 'var(--ok, var(--green))' }}>Saved.</span>}
            <button className="btn btn-accent" disabled={busy} onClick={() => void save()}
              style={{ padding: '9px 20px' }}>
              {busy ? 'Saving…' : 'Save the register'}
            </button>
          </div>
        </div>
      )}

      {/* ── late / excused ── */}
      {menuFor && (
        <div onClick={() => setMenuFor(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 60,
          display: 'grid', placeItems: 'center', padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} className="card"
            style={{ width: 300, maxWidth: '100%', padding: 18 }}>
            <div style={{ fontWeight: 650, marginBottom: 2 }}>{menuFor.fullName}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
              {menuFor.admissionNumber}
            </div>
            {(['Present', 'Absent', 'Late', 'Excused'] as Status[]).map((v) => (
              <button key={v} className="btn" style={{ width: '100%', marginBottom: 6 }}
                onClick={() => set(menuFor.studentId, v)}>{v}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
